/**
 * Create Event Modal
 *
 * Modal for creating a new guest upload event.
 * Shows QR code and share link after successful creation.
 */

import { useState, useEffect } from "react";
import { useSnapshot } from "valtio";
import { toast } from "sonner";
import {
  eventsStore,
  createEvent,
  clearNewEventResponse,
  type CreateEventRequest,
} from "./stores/events";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Copy, ExternalLink, Download, CheckCircle2 } from "lucide-react";

interface CreateEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = "form" | "success";

export function CreateEventModal({
  open,
  onOpenChange,
}: CreateEventModalProps) {
  const { createLoading, newEventResponse } = useSnapshot(eventsStore);
  const [step, setStep] = useState<Step>("form");
  const [name, setName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [maxUploads, setMaxUploads] = useState(100);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setStep("form");
      setName("");
      setExpiresInDays(7);
      setMaxUploads(100);
      clearNewEventResponse();
    }
  }, [open]);

  // Show success step when event is created
  useEffect(() => {
    if (newEventResponse) {
      setStep("success");
    }
  }, [newEventResponse]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Please enter an event name");
      return;
    }

    const request: CreateEventRequest = {
      name: name.trim(),
      expiresInDays,
      maxUploads,
    };

    const result = await createEvent(request);
    if (result) {
      toast.success("Event created successfully!");
    }
  };

  const handleCopyLink = () => {
    if (newEventResponse) {
      navigator.clipboard.writeText(newEventResponse.uploadUrl);
      toast.success("Upload link copied to clipboard");
    }
  };

  const handleDownloadQR = () => {
    if (newEventResponse) {
      const link = document.createElement("a");
      link.href = newEventResponse.qrCodeUrl;
      link.download = `${newEventResponse.name.replace(/\s+/g, "_")}_QR.png`;
      link.click();
    }
  };

  const handleClose = () => {
    clearNewEventResponse();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {step === "form" ? (
          <>
            <DialogHeader>
              <DialogTitle>Create Guest Upload Event</DialogTitle>
              <DialogDescription>
                Create an event where guests can upload videos without an
                account.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Event Name */}
              <div className="space-y-2">
                <Label htmlFor="event-name">Event Name</Label>
                <Input
                  id="event-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sarah's Birthday Party"
                  required
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  This name will be shown to guests when they upload.
                </p>
              </div>

              {/* Expiration */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Expires in</Label>
                  <span className="text-sm font-medium">
                    {expiresInDays} days
                  </span>
                </div>
                <Slider
                  value={[expiresInDays]}
                  onValueChange={([value]) => setExpiresInDays(value)}
                  min={1}
                  max={30}
                  step={1}
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground">
                  Videos will be automatically deleted after expiration.
                </p>
              </div>

              {/* Max Uploads */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Maximum uploads</Label>
                  <span className="text-sm font-medium">{maxUploads}</span>
                </div>
                <Slider
                  value={[maxUploads]}
                  onValueChange={([value]) => setMaxUploads(value)}
                  min={10}
                  max={500}
                  step={10}
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground">
                  Limit the total number of uploads for this event.
                </p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={createLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createLoading}>
                  {createLoading ? "Creating..." : "Create Event"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Event Created!
              </DialogTitle>
              <DialogDescription>
                Share this QR code or link with your guests.
              </DialogDescription>
            </DialogHeader>

            {newEventResponse && (
              <div className="space-y-4">
                {/* QR Code */}
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img
                    src={newEventResponse.qrCodeUrl}
                    alt={`QR Code for ${newEventResponse.name}`}
                    className="w-48 h-48"
                  />
                </div>

                {/* Event Info */}
                <div className="text-center space-y-1">
                  <p className="font-semibold">{newEventResponse.name}</p>
                  <p className="text-3xl font-mono font-bold tracking-wider">
                    {newEventResponse.code}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Expires in {newEventResponse.expiresInDays} days •{" "}
                    {newEventResponse.maxUploads} uploads max
                  </p>
                </div>

                {/* Upload URL */}
                <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                  <code className="flex-1 text-sm truncate">
                    {newEventResponse.uploadUrl}
                  </code>
                  <Button variant="ghost" size="icon" onClick={handleCopyLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      window.open(newEventResponse.uploadUrl, "_blank")
                    }
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <DialogFooter className="flex gap-2 sm:justify-between">
              <Button variant="outline" onClick={handleDownloadQR}>
                <Download className="h-4 w-4 mr-2" />
                Download QR
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCopyLink}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
                <Button onClick={handleClose}>Done</Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
